/**
 * @description Defines a logging service that logs messages to the console.
 * The logger groups can be enabled or disabled, and the logging can be enabled or disabled in the employee settings menu.
 * The logger groups and logging state are saved to local storage.
 * The logger groups are defined in the `loggerGroups` object.
 * The enabled logger groups are logged by default in development.
 */
import { reactive, watch, ref } from 'vue'

/**
 * The logger groups and their default visibility in development.
 * The group names will be shown in the employee settings menu.
 */
export const loggerGroups = reactive({
  /** The Logger group is used for general logging */
  Logger: true,
  /** Feature flag logs */
  'Feature Flag Service': true,
  /** Identity auth */
  Identity: true,
  /** Onboarding logs */
  Onboarding: true,
  /** Google Tag Manager , Google Analytics logs */
  'GTM Marketing': true,
  /** Marketing logs */
  Marketing: true,
  /** Sentry debug mode */
  Sentry: false,
  /** Firebase analytics logs */
  'Firebase Logging': true,
  /** Map related logs */
  Map: true,
  /** Router related logs */
  Router: true,
  /** PubSub event logs */
  PubSub: false,
  /** Wind related logs */
  Wind: false,
  /** Subscription related logs */
  Subscription: true,
  /** Eppo related logs */
  Eppo: true,
})

const isDevelopment = process.env.NODE_ENV === 'development'
// Enable logging by default in development
export const isLoggingEnabled = ref(isDevelopment)

// Create a type from the keys of the object
export type LoggerGroup = keyof typeof loggerGroups
type loggerGroupsType = typeof loggerGroups

export const DEFAULT_LOG_STYLE = 'color: #0085FF;  font-weight: bold;'

/**
 * Enables logging
 */
export function enableLogging() {
  isLoggingEnabled.value = true
}

/**
 * Disables logging
 */
export function disableLogging() {
  isLoggingEnabled.value = false
}

/**
 * Enables logging for a specific logger group.
 *
 * @param group - The logger group to enable.
 */
export function enableGroup(group: LoggerGroup) {
  loggerGroups[group] = true
}

/**
 * Disables logging for a specific logger group.
 *
 * @param group - The logger group to disable.
 */
export function disableGroup(group: LoggerGroup) {
  loggerGroups[group] = false
}

/**
 * Checks if a specific logger group is enabled.
 *
 * @param group - The logger group to check.
 * @returns - Returns `true` if the logger group is enabled, otherwise `false`.
 */
export function isGroupEnabled(group: LoggerGroup): boolean {
  return loggerGroups[group] === true
}

/**
 * @private
 * The key used to store the logger groups in local storage.
 * Only exported for testing purposes.
 */
export const storageKey = 'onx-devex-loggerGroups'

/**
 * @private
 * Loads logger groups from local storage.
 * Only exported for testing purposes.
 */
export function loadGroupsFromLocalStorage() {
  // Load logger groups from local storage
  const storedGroups = localStorage?.getItem(storageKey)
  if (storedGroups) {
    let parsedGroups: loggerGroupsType
    try {
      parsedGroups = JSON.parse(storedGroups)
      if (parsedGroups) {
        Object.keys(parsedGroups).forEach((key) => {
          // Check if the key is a valid logger group
          if (key in loggerGroups) {
            loggerGroups[key as LoggerGroup] = parsedGroups[key as LoggerGroup]
          }
        })
      }
    } catch (e) {
      warn('Logger', 'Failed to parse stored logger groups', e)
    }
  }
}
// Load logger groups from local storage
loadGroupsFromLocalStorage()

/**
 * @private
 * Saves logger groups to local storage.
 * Only exported for testing purposes.
 *
 * @param groups - The logger groups to save to local storage.
 */
export function saveGroupsToLocalStorage(groups: loggerGroupsType) {
  localStorage?.setItem(storageKey, JSON.stringify(groups))
}

// Watch for group visibility changes and save to local storage
watch(loggerGroups, (newGroups) => {
  saveGroupsToLocalStorage(newGroups)
})

export const isLoggingEnabledKey = 'onx-devex-isLoggingEnabled'
/**
 * @private
 * Loads logging enabled state from local storage.
 * Only exported for testing purposes.
 */
export function loadIsLoggingEnabledFromLocalstorage() {
  const storedIsLoggingEnabled = localStorage?.getItem(isLoggingEnabledKey)
  if (storedIsLoggingEnabled) {
    isLoggingEnabled.value = JSON.parse(storedIsLoggingEnabled)
  }
}

// Load logging enabled state from local storage
loadIsLoggingEnabledFromLocalstorage()
/**
 * Saves the logging enabled state to local storage.
 * @private
 * @param isLoggingEnabled
 */
export function saveIsLoggingEnabledToLocalStorage(isLoggingEnabled: boolean) {
  localStorage?.setItem(isLoggingEnabledKey, JSON.stringify(isLoggingEnabled))
}

// Watch for logging enabled state changes and save to local storage
watch(isLoggingEnabled, (newIsLoggingEnabled) => {
  saveIsLoggingEnabledToLocalStorage(newIsLoggingEnabled)
})

type LogLevel = 'error' | 'warn' | 'info'

/**
 * Logs a message to the console if the specified logging group is enabled.
 *
 * @param group - The logging group to which the message belongs.
 * @param level - The level of the log message (e.g., 'log', 'warn', 'error').
 * @param args - The arguments to be logged.
 */
function log(group: LoggerGroup, level: LogLevel, ...args: unknown[]) {
  if (loggerGroups[group] === undefined) {
    error(
      'Logger',
      `Group "${group}" does not exist and won't be logged. Add it to the loggerGroups object in logger.ts`
    )
  }
  const isEnabled = isGroupEnabled(group)
  if (isLoggingEnabled.value && isEnabled) {
    // eslint-disable-next-line no-console
    console[level](`%c${group}`, DEFAULT_LOG_STYLE, ...args)
  }
}

/**
 * Logs an informational message for the specified logger group.
 *
 * @param group - The logger group to which the message should be logged.
 * @param args - The arguments to be logged.
 */
export function info(group: LoggerGroup, ...args: unknown[]) {
  log(group, 'info', ...args)
}

/**
 * Logs a warning message for the specified logger group.
 *
 * @param group - The logger group to which the warning message belongs.
 * @param args - The arguments to be logged as a warning message.
 */
export function warn(group: LoggerGroup, ...args: unknown[]) {
  log(group, 'warn', ...args)
}

/**
 * Logs an error message for the specified logger group.
 *
 * @param group - The logger group to which the error message belongs.
 * @param args - Additional arguments to be logged with the error message.
 */
export function error(group: LoggerGroup, ...args: unknown[]) {
  log(group, 'error', ...args)
}
